import { ChangeEvent, ReactElement, useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper, TextField, Grid
} from "@mui/material";
import { IFamilyBase, IFamilyGroup } from "../../types";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";

interface IProps {
  family: IFamilyBase;
  customProducts: number[];
  allowEdit: boolean;
  right: IFamilyGroup[];
  setRight: (right: IFamilyGroup[]) => void;
}

interface ICustomListProps {
  items: IFamilyGroup[];
}

const not = (a: IFamilyGroup[], b: IFamilyGroup[]) => {
  return a.filter((value) => b.indexOf(value) === -1);
};

const intersection = (a: IFamilyGroup[], b: IFamilyGroup[]) => {
  return a.filter((value) => b.indexOf(value) !== -1);
};

const CustomProductPanel = ({ family, customProducts, allowEdit, right, setRight }: IProps): ReactElement => {
  const [checked, setChecked] = useState<IFamilyGroup[]>([]);
  const [left, setLeft] = useState<IFamilyGroup[]>([]);

  useEffect(() => {
    setLeft(not(family.products, right));
  }, [ family, customProducts ]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: IFamilyGroup) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    setRight(right.concat(left));
    setLeft([]);
  };

  const handleCheckedRight = () => {
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setRight([]);
  };

  const CustomList = ({ items }: ICustomListProps): ReactElement => {
    const [ filter, setFilter ] = useState<string>("");

    const onchangeFilter = (event: ChangeEvent<HTMLInputElement>) => setFilter(event.target.value);

    return (
      <div style={{  width: 240, }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder="Filter"
          value={filter}
          onChange={onchangeFilter}
          fullWidth={true}
          slotProps={{
            input: {
            startAdornment:
              <InputAdornment position="start">
                <SearchIcon/>
              </InputAdornment>
          }}}
        />

        <Paper elevation={0} style={{ width: 240, height: 400, overflow: "auto", marginTop: ".5em" }}>
          <List dense={true} component="div" role="list" >
            {items.filter(x => x.productName.toLowerCase().includes(filter.toLowerCase())).map(value => {
              const labelId = `transfer-list-item-${value}-label`;

              return (
                <ListItemButton key={`value-${value.familyGroupID}`} onClick={handleToggle(value)} className="p-0">
                  <ListItemIcon>
                    <Checkbox
                      checked={checked.indexOf(value) !== -1}
                      tabIndex={-1}
                      disableRipple={true}
                      disabled={!allowEdit}
                      aria-labelledby={labelId}
                    />
                  </ListItemIcon>
                  <ListItemText id={labelId} primary={value.productName}/>
                </ListItemButton>
              );
            })}
            <ListItem/>
          </List>
        </Paper>
      </div>);
  };

  return (
    <div className="m-1 display-flex-row justify-content-center align-items-center gap-1" >
      <CustomList items={left} />
      <Grid sx={{ alignItems: "center" }}>
        <Grid sx={{ flexDirection: "column", alignItems: "center" }}>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={!allowEdit || left.length === 0}
            aria-label="move all right">
            ≫
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={!allowEdit || leftChecked.length === 0}
            aria-label="move selected right">
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={!allowEdit || rightChecked.length === 0}
            aria-label="move selected left">
            &lt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={!allowEdit || right.length === 0}
            aria-label="move all left">
            ≪
          </Button>
        </Grid>
      </Grid>
      <Grid>
       <CustomList items={right} />
      </Grid>
    </div>
  );
};

export default CustomProductPanel;
